import { useMutation, useSuspenseQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { jwtDecode } from 'jwt-decode'
import { Loader, TriangleAlert } from 'lucide-react'
import { type ReactNode, use, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { AuthContext, AuthDispatchContext } from '@/components/auth/context'
import { PasswordInput } from '@/components/sv/password-input'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { gitAuthUrls } from '@/config/git-auth-urls'
import { gql } from '@/lib/graphql/gql'
import { BitBucketLogo, GitHubLogo, GitLabLogo, SevallaLogo } from '@/logos'
import type { GitType } from '@/graphql.types'
import type {
  AcceptInvitationMutation,
  AcceptInvitationMutationVariables,
  InvitationQuery,
  InvitationQueryVariables,
  RegisterMutation,
  RegisterMutationVariables,
} from '@/routes/user-management/accept-invitation/index.types.gen'

const GIT_PROVIDERS: Array<{
  name: string
  gitType: GitType
  icon: ReactNode
  authUrl: string
}> = [
  {
    name: 'GitHub',
    gitType: 'github',
    icon: <GitHubLogo />,
    authUrl: gitAuthUrls.github,
  },
  {
    name: 'GitLab',
    gitType: 'gitlab',
    icon: <GitLabLogo />,
    authUrl: gitAuthUrls.gitlab,
  },
  {
    name: 'Bitbucket',
    gitType: 'bitbucket',
    icon: <BitBucketLogo />,
    authUrl: gitAuthUrls.bitbucket,
  },
]

const ACCEPT_INVITATION_MUTATION = gql`
  mutation AcceptInvitation($idInvitation: String!) {
    acceptInvitation(idInvitation: $idInvitation)
  }
`

const REGISTER_MUTATION = gql`
  mutation Register(
    $email: String!
    $firstName: String!
    $lastName: String!
    $password: String!
    $idInvitation: String!
  ) {
    registerViaInvitation(
      email: $email
      firstName: $firstName
      lastName: $lastName
      password: $password
      idInvitation: $idInvitation
    ) {
      token
      token
      authError {
        code
      }
    }
  }
`

const GET_INVITATION = gql`
  query Invitation(
    $idInvitation: String!
    $idUser: String!
    $isAuthenticated: Boolean!
  ) {
    invitationByEncodedId(idEncoded: $idInvitation) @live {
      email
      status
      invitedByFullName
      invitedByImage
      companyName
      isAlreadyRegistered
      idCompany
    }
    user(id: $idUser) @include(if: $isAuthenticated) @live {
      id
      email
    }
  }
`

const schema = z.object({
  email: z.email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  password: z
    .string('Password is required')
    .regex(
      /^(?=.{8,})(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).*$/,
      'Password must contain a min. of 8 characters, at least one lowercase and capital letter, and a number.'
    )
    .min(8, 'Password must be at least 8 characters long')
    .max(150, 'Password must be at most 150 characters long'),
})

const gitErrorMap = {
  EMAIL_ADDRESS_ALREADY_REGISTERED:
    'The email address attached to GIT account is already registered. Please login instead or use another GIT account.',
  NO_EMAIL_PROVIDED_BY_GIT_SERVICE:
    'The GIT service did not provide an email address. Please use the form below to register.',
  EMAIL_MISMATCH:
    'The email address attached to GIT account does not match the invitation email. Please use the form below to register.',
  FAILED_GIT_AUTH:
    'Failed to authenticate with the GIT service. Please try again or use the form below to register.',
}

export const AcceptInvitation = () => {
  const { idInvitation = '' } = useParams()
  const [searchParams] = useSearchParams()
  const error = searchParams.get('error') ?? ''
  const token = searchParams.get('token') ?? ''
  const gitError = error ? gitErrorMap[error as keyof typeof gitErrorMap] : null
  const [isPending, startTransition] = useTransition()
  const navigate = useNavigate()
  const { isAuthenticated = false, idUser = '' } = use(AuthContext) ?? {}
  const { login } = use(AuthDispatchContext) ?? {}
  const [gitLoading, setGitLoading] = useState<GitType | null>(null)

  const { data } = useSuspenseQuery<InvitationQuery, InvitationQueryVariables>(
    GET_INVITATION,
    {
      variables: { idUser: idUser ?? '', idInvitation, isAuthenticated },
      errorPolicy: 'ignore',
    }
  )

  const [acceptInvitationMutation] = useMutation<
    AcceptInvitationMutation,
    AcceptInvitationMutationVariables
  >(ACCEPT_INVITATION_MUTATION)

  const [register] = useMutation<RegisterMutation, RegisterMutationVariables>(
    REGISTER_MUTATION
  )

  const invitation = data?.invitationByEncodedId
  const isInvalidInvitation = !invitation || invitation.status === 'CANCELLED'

  const state = encodeURIComponent(
    JSON.stringify({
      action: 'signup',
      api: import.meta.env.VITE_GRAPHQL_SERVER_URI,
      redirectUri: window.location.origin + `/acceptinvitation/${idInvitation}`,
      invitationToken: idInvitation,
    })
  )

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: invitation?.email ?? '',
      password: '',
    },
  })
  const loadingGitProvider = GIT_PROVIDERS.find(
    ({ gitType }) => gitType === gitLoading
  )
  const isFormBusy = isPending || Boolean(loadingGitProvider)
  const statusMessage = isPending
    ? 'Registering and accepting invitation...'
    : loadingGitProvider
      ? `Redirecting to ${loadingGitProvider.name}...`
      : ''

  if (token) {
    try {
      jwtDecode(token)
      login?.(token)
      return <Navigate to="/" replace />
    } catch {
      return <Navigate to="/login" replace />
    }
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const onSubmit = async (data: z.infer<typeof schema>) => {
    const isValid = await form.trigger()
    if (isPending || !isValid) {
      return
    }
    startTransition(async () => {
      try {
        const res = await register({
          variables: {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            password: data.password,
            idInvitation,
          },
        })
        const token = res.data?.registerViaInvitation?.token
        if (token) {
          try {
            jwtDecode(token)
            login?.(token)
            navigate('/', { replace: true })
          } catch (err) {
            navigate('/login', { replace: true })
            console.error('Error decoding token:', err)
          }
        }
      } catch {
        toast(
          <ToasterContent variant="error">Failed to register</ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  const acceptInvitation = async () => {
    await acceptInvitationMutation({ variables: { idInvitation } })
    navigate('/')
  }

  if (
    invitation &&
    data?.user &&
    invitation?.email === data?.user?.email &&
    invitation?.status === 'PENDING'
  ) {
    acceptInvitation()
    return <div />
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <a
          href="https://sevalla.com"
          className="flex items-center gap-2 self-center"
        >
          <SevallaLogo className="w-24 h-auto" />
        </a>
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="font-display text-xl">
                Register & accept invitation
              </CardTitle>
              <CardDescription>Last step before you're in!</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  aria-busy={isFormBusy}
                  className="space-y-6"
                >
                  <div
                    className="sr-only"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {statusMessage}
                  </div>
                  {gitError ? (
                    <Alert variant="destructive">
                      <TriangleAlert />
                      <AlertTitle>Failed GIT signup</AlertTitle>
                      <AlertDescription>{gitError}</AlertDescription>
                    </Alert>
                  ) : null}
                  {invitation?.isAlreadyRegistered ? (
                    <Alert variant="destructive">
                      <TriangleAlert />
                      <AlertTitle>Already registered</AlertTitle>
                      <AlertDescription>
                        <div>
                          This email address is already registered. Please{' '}
                          <Link
                            to="/login"
                            className="underline underline-offset-4"
                          >
                            login
                          </Link>{' '}
                          instead.
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  {!invitation ? (
                    <Alert variant="destructive">
                      <TriangleAlert />
                      <AlertTitle>Invalid invitation</AlertTitle>
                      <AlertDescription>
                        Please check the link or contact the person who invited
                        you.
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  {invitation?.status === 'ACCEPTED' ? (
                    <Alert variant="destructive">
                      <TriangleAlert />
                      <AlertTitle>Accepted invitation</AlertTitle>
                      <AlertDescription>
                        <div>This invitation has already been accepted.</div>
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  {invitation?.status === 'CANCELLED' ? (
                    <Alert variant="destructive">
                      <TriangleAlert />
                      <AlertTitle>Cancelled invitation</AlertTitle>
                      <AlertDescription>
                        <div>
                          This invitation has been cancelled. Please contact the
                          person who invited you for more information.
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  <div className="grid gap-6">
                    <div className="grid grid-cols-3 gap-4">
                      {GIT_PROVIDERS.map((git) => (
                        <Button
                          key={git.gitType}
                          aria-label={
                            gitLoading === git.gitType
                              ? `Redirecting to ${git.name}...`
                              : `Continue with ${git.name}`
                          }
                          variant="outline"
                          disabled={
                            Boolean(gitLoading) || Boolean(isInvalidInvitation)
                          }
                          type="button"
                          onClick={() => {
                            setGitLoading(git.gitType)
                            window.location.href = `${git.authUrl}&state=${state}`
                          }}
                        >
                          {gitLoading === git.gitType ? (
                            <Loader
                              className="animate-spin"
                              aria-hidden="true"
                            />
                          ) : (
                            git.icon
                          )}
                          {git.name}
                        </Button>
                      ))}
                    </div>
                    <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                      <span className="bg-card text-muted-foreground relative z-10 px-2">
                        Or continue with
                      </span>
                    </div>
                    <div className="grid gap-6">
                      <div className="flex items-start gap-4">
                        <FormField
                          control={form.control}
                          name="firstName"
                          disabled={Boolean(isInvalidInvitation)}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>First name</FormLabel>
                              <FormControl>
                                <Input
                                  autoComplete="off"
                                  aria-label="First name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="lastName"
                          disabled={Boolean(isInvalidInvitation)}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Last name</FormLabel>
                              <FormControl>
                                <Input
                                  autoComplete="off"
                                  aria-label="Last name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                disabled
                                autoComplete="off"
                                aria-label="Email address"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="password"
                        disabled={Boolean(isInvalidInvitation)}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <PasswordInput autoComplete="off" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full"
                        aria-label={
                          isPending
                            ? 'Registering and accepting invitation...'
                            : 'Register and accept invitation'
                        }
                        disabled={isPending || Boolean(isInvalidInvitation)}
                        onClick={form.handleSubmit(onSubmit)}
                      >
                        {isPending ? (
                          <Loader className="animate-spin" aria-hidden="true" />
                        ) : null}
                        Register & accept invitation
                      </Button>
                    </div>
                  </div>
                </form>
              </Form>
              <div className="mt-4 text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank">
                  Terms of Service
                </a>{' '}
                apply.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
